import { handle } from 'redux-pack';
import { Action } from '../../../types/actions';
import { BrandliveEvent, EventGroup, EventGroupAssociation } from '../../../types/working-model';
import {
	ADD_ADMIN_EVENT,
	CLEAR_EVENTS,
	GET_EVENTS,
	UPDATE_ADMIN_EVENT,
	UPDATE_EVENTS_NAME,
	REMOVE_SESSION_FROM_EVENT,
	REMOVE_EVENT_FROM_EVENTS,
	SET_EVENTS_LOADING,
	GET_EVENT_GROUPS,
	CREATE_EVENT_GROUP,
	ADD_EVENTS_TO_EVENT_GROUP,
	UPDATE_EVENT_GROUP,
	DELETE_EVENT_GROUP,
	REMOVE_EVENT_FROM_EVENT_GROUPS,
	SET_SELECTED_EVENT_GROUP,
	CLEAR_GROUP_ERROR,
} from '../../actions/admin/events';

export interface AdminEventsState {
	events: BrandliveEvent[];
	eventsError: boolean;
	eventsLoading: boolean;
	eventGroups: EventGroup[];
	selectedEventGroup: string;
	groupError: string;
}

const initialState: AdminEventsState = {
	events: [],
	eventsError: false,
	eventsLoading: false,
	eventGroups: [],
	selectedEventGroup: '',
	groupError: '',
};

export default function EventsReducer(
	state: AdminEventsState = initialState,
	action: Action
): AdminEventsState {
	switch (action.type) {
		case GET_EVENTS: {
			return handle(state, action, {
				success: (state) => ({
					...state,
					events: action.payload,
					eventsError: false,
				}),
				start: (state) => ({ ...state, eventsLoading: true }),
				failure: (state) => ({ ...state, eventsError: true }),
				finish: (state) => ({ ...state, eventsLoading: false }),
			});
		}
		case ADD_ADMIN_EVENT: {
			return {
				...state,
				events: [...state.events, action.payload]
			};
		}
		case UPDATE_ADMIN_EVENT: {
			const updatedEvent: BrandliveEvent = action.payload;
			return {
				...state,
				events: state.events.map((event) => {
					if (event.uuid === updatedEvent.uuid) { return updatedEvent; }
					return event;
				}),
			};
		}
		case UPDATE_EVENTS_NAME: {
			const updatedEvent: BrandliveEvent = action.payload;
			return {
				...state,
				events: state.events.map((event) => {
					if (event.event === updatedEvent.event) {
						return {
							...event,
							name: updatedEvent.name,
						};
					}
					return event;
				}),
			};
		}
		case CLEAR_EVENTS: {
			return {
				...state,
				events: []
			};
		}
		case REMOVE_SESSION_FROM_EVENT: {
			const matchedEvent = state.events.find(event => event.uuid === action.payload.eventUuid);
			if (!matchedEvent) return state;
			return {
				...state,
				events: state.events.map(event => {
					if (event.uuid !== action.payload.eventUuid) return event;
					return {
						...event,
						sessions: event.sessions.filter(session => session.session !== action.payload.sessionID),
					};
				}),
			};
		}
		case REMOVE_EVENT_FROM_EVENTS: {
			return {
				...state,
				events: state.events.filter(event => event.uuid !== action.payload),
			};
		}
		case SET_EVENTS_LOADING: {
			return {
				...state,
				eventsLoading: action.payload
			};
		}
		case GET_EVENT_GROUPS: {
			return handle(state, action, {
				success: state => ({
					...state,
					eventGroups: action.payload,
				})
			});
		}

		case CREATE_EVENT_GROUP: {
			return handle(state, action, {
				success: state => ({
					...state,
					groupError: '',
					eventGroups: [...state.eventGroups, action.payload],
				}),
				failure: state => {
					return {
						...state,
						groupError: action.payload,
					};
				},
			});
		}

		case ADD_EVENTS_TO_EVENT_GROUP: {
			return handle(state, action, {
				success: state => {
					const groupUuid = action.payload[0].group_uuid;
					const eventUuids = action.payload.map((ega: EventGroupAssociation) => ega.event_uuid);

					return ({
						...state,
						eventGroups: state.eventGroups.map(eg => eg.uuid === groupUuid
							? { ...eg, event_uuids: [...eg.event_uuids, ...eventUuids] }
							: eg
						)
					});
				}
			});
		}

		case UPDATE_EVENT_GROUP: {
			return handle(state, action, {
				success: state => ({
					...state,
					groupError: '',
					eventGroups: state.eventGroups.map(eg => eg.uuid === action.payload.uuid ? action.payload : eg)
				}),
				failure: state => {
					return {
						...state,
						groupError: action.payload,
					};
				},
			});
		}

		case CLEAR_GROUP_ERROR: {
			return {
				...state,
				groupError: ''
			};
		}

		case DELETE_EVENT_GROUP: {
			return handle(state, action, {
				success: state => {
					const undeletedEventGroups = state.eventGroups.filter(eg => eg.uuid !== action.payload.deletedGroupUuid);
					const eventUuids = undeletedEventGroups.flatMap(eg => eg.event_uuids);

					return {
						...state,
						events: state.events.filter(event => eventUuids.includes(event.uuid)),
						eventGroups: undeletedEventGroups
					};
				}
			});
		}

		case REMOVE_EVENT_FROM_EVENT_GROUPS: {
			return {
				...state,
				eventGroups: state.eventGroups.map(eg => ({
					...eg,
					event_uuids: eg.event_uuids.filter(uuid => uuid !== action.payload)
				}))
			};
		}

		case SET_SELECTED_EVENT_GROUP: {
			return {
				...state,
				selectedEventGroup: action.payload
			};
		}

		default:
			return state;
	}
}
